import React, { type PropsWithChildren, useMemo } from "react";
import { PageMessage } from "@picklebet/ui";
import { useIsMobile } from "@/hooks/useDisplay";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { ReactComponent as EmptyIcon } from "@/features/betslip/assets/empty.svg";
import { EmptyWithActiveGameSessions } from "@/features/betslip/components/EmptyWithActiveGameSessions";
import { useGameSessions } from "@/features/casino/hooks/useGameSessions";
import { LinkButton } from "@/features/shell/components/shared/LinkButton/LinkButton";
import { ButtonGroup } from "@picklebet/ui";
import useCanPlayPickems from "@/hooks/useCanPlayPickems";
import type { buttonVariants } from "@picklebet/ui";
import type { VariantProps } from "class-variance-authority";
import * as styles from "@/features/betslip/components/Empty.module.scss";

const StatusMessage = ({
  children,
  btnVariant,
}: PropsWithChildren<{
  btnVariant?: VariantProps<typeof buttonVariants>["variant"];
}>) => {
  const isMobile = useIsMobile();
  const variant = btnVariant || isMobile ? "primary" : "blurred";
  const { hasActiveGameSession } = useGameSessions(true);
  const viewCasino = usePermission("viewCasino");
  const playCasino = usePermission("playPaidCasino");
  const submitBets = usePermission("submitBet");
  const viewBettingMarkets = usePermission("viewBettingMarkets");
  const pickemsAvailable = useCanPlayPickems();

  const casinoAvailable =
    viewCasino &&
    viewCasino === "GRANTED" &&
    playCasino &&
    playCasino === "GRANTED";

  const canMakeSportsbookBets =
    submitBets === "GRANTED" && viewBettingMarkets === "GRANTED";

  if (hasActiveGameSession) {
    return <EmptyWithActiveGameSessions />;
  }

  if (!canMakeSportsbookBets && (casinoAvailable || pickemsAvailable)) {
    return (
      <PageMessage
        image={<EmptyIcon />}
        title={`Betting is not available`}
        subTitle={`Check out what else we have`}
        className={styles.container}
      >
        <ButtonGroup>
          {casinoAvailable && (
            <LinkButton
              size={isMobile ? "sm" : "md"}
              to={`/casino/lobby`}
              variant={variant}
            >
              Casino
            </LinkButton>
          )}
          {pickemsAvailable && (
            <LinkButton
              size={isMobile ? "sm" : "md"}
              to={`/pickems`}
              variant={variant}
            >
              Pick'ems
            </LinkButton>
          )}
        </ButtonGroup>
      </PageMessage>
    );
  }

  return children;
};

export { StatusMessage };
